import React from 'react';
import { Network, Compass, Calendar, Building, Handshake } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { serviceImages } from '../data/translations';

const iconMap = {
  network: Network,
  compass: Compass,
  calendar: Calendar,
  building: Building,
  handshake: Handshake,
};

// France flag SVG component
const FranceFlag = ({ className }) => (
  <svg viewBox="0 0 30 20" className={className}>
    <rect width="10" height="20" fill="#002395" />
    <rect x="10" width="10" height="20" fill="#FFFFFF" />
    <rect x="20" width="10" height="20" fill="#ED2939" />
  </svg>
);

// Latvia flag SVG component
const LatviaFlag = ({ className }) => (
  <svg viewBox="0 0 30 20" className={className}>
    <rect width="30" height="20" fill="#9E3039" />
    <rect y="8" width="30" height="4" fill="#FFFFFF" />
  </svg>
);

const ServiceCard = ({ service }) => {
  const IconComponent = iconMap[service.icon] || Network;
  const imageUrl = serviceImages[service.icon];
  const isHandshake = service.icon === 'handshake';

  return (
    <div className="group relative overflow-hidden border border-[#3f4816]/50 bg-[#302f2c]/30 transition-all duration-300 hover:border-[#d9fb06]/30">
      {/* Image */}
      <div className="relative h-40 sm:h-48 overflow-hidden">
        <img
          src={imageUrl}
          alt={service.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1c1b] via-[#1a1c1b]/60 to-transparent" />
        
        {/* Flags overlay for handshake card */}
        {isHandshake && (
          <>
            {/* France flag - top left */}
            <div className="absolute top-3 left-3 shadow-lg rounded overflow-hidden border border-white/20">
              <FranceFlag className="w-10 h-7" />
            </div>
            {/* Latvia flag - top right */}
            <div className="absolute top-3 right-3 shadow-lg rounded overflow-hidden border border-white/20">
              <LatviaFlag className="w-10 h-7" />
            </div>
          </>
        )}
        
        {/* Icon overlay */}
        <div className="absolute bottom-4 left-4">
          <div className="w-12 h-12 rounded-full border border-[#d9fb06]/50 bg-[#1a1c1b]/80 backdrop-blur-sm flex items-center justify-center">
            <IconComponent className="w-5 h-5 text-[#d9fb06]" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="font-bold text-[#d9fb06] text-lg mb-3">
          {service.title}
        </h3>
        <p className="text-[#888680] text-sm leading-relaxed">
          {service.description}
        </p>
      </div>
    </div>
  );
};

const ServicesSection = () => {
  const { t } = useLanguage();

  return (
    <section id="services" className="bg-[#1a1c1b] py-20 md:py-28 border-t border-[#3f4816]/30">
      <div className="max-w-[87.5rem] mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div className="mb-12">
          <h2 className="font-black text-[#d9fb06] text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.85]">
            {t.services.title}
          </h2>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.services.items.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
